import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuthService } from '../../../services/auth/auth.service';
import { FrontendLoggerService } from '../../../services/frontend-logger.service';
import { LanguageService } from '../../../services/language/language.service';
import { ThemeService } from '../../../services/theme/theme.service';
import { UserService } from '../../../services/user.service';
import { WizardOnboardingTokenVerifier } from './wizard-onboarding-token.verifier';

interface SignupProvisioningErrorBody {
  error?: string;
  message?: string;
  details?: { supportReference?: string };
}

export interface WizardCompletionPort {
  setLoading(value: boolean): void;
  setError(message: string): void;
  handleProvisioningFailure(error: any): boolean;
  translate(key: string): string;
}

export interface SignupFailurePresentation {
  handled: boolean;
  message: string;
  supportReference: string | null;
}

export class WizardOnboardingSubmitter {
  constructor(
    private readonly authService: AuthService,
    private readonly auth0: Auth0Service,
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly themeService: ThemeService,
    private readonly languageService: LanguageService,
    private readonly logger: FrontendLoggerService,
    private readonly tokenVerifier: WizardOnboardingTokenVerifier
  ) {}

  submit(form: FormGroup, port: WizardCompletionPort): void {
    port.setLoading(true);
    port.setError('');
    const formData = form.value;
    formData.name = `${formData.firstName} ${formData.secondName}`.trim();
    formData.email = '';

    this.auth0.idTokenClaims$.pipe(take(1)).subscribe({
      next: (claims: any) => this.submitWithClaims(formData, claims, port),
      error: (error) => {
        this.logger.error('Error reading ID token claims', error);
        port.setLoading(false);
        port.setError(port.translate('wizard.save_error'));
      },
    });
  }

  async loginWithRedirectToHome(): Promise<void> {
    const result = (this.auth0 as any).loginWithRedirect({ appState: { target: '/home' } });
    if (result && typeof result.subscribe === 'function') {
      await firstValueFrom(result);
      return;
    }
    await result;
  }

  describeProvisioningFailure(
    error: any,
    translate: (key: string) => string
  ): SignupFailurePresentation {
    const body = error?.error as SignupProvisioningErrorBody | undefined;
    if (error?.status !== 503 || body?.error !== 'signup_provisioning_failed') {
      return { handled: false, message: '', supportReference: null };
    }

    const supportReference = body.details?.supportReference?.trim() || null;
    if (supportReference) {
      this.logger.error(
        `[Wizard] Signup provisioning failed. supportReference=${supportReference}`,
        'Wizard.completeOnboarding'
      );
    }
    return {
      handled: true,
      message: body.message || translate('wizard.save_error'),
      supportReference,
    };
  }

  private submitWithClaims(formData: any, claims: any, port: WizardCompletionPort): void {
    formData.email = claims?.email || '';
    this.logger.info('[Wizard] Datos del formulario preparados');
    this.themeService.setTheme(formData.theme);
    this.languageService.setLanguage(formData.language);

    const request = {
      firstName: formData.firstName,
      lastName: formData.secondName,
      country: formData.country,
      jobTitle: formData.jobTitle,
      company: formData.company,
      organizationType: formData.organizationType,
      organizationSize: formData.organizationSize,
      email: formData.email,
      Language: formData.language,
      theme: formData.theme,
      TimeZone: formData.timezone,
      role: claims?.role || '',
    };

    this.logger.info('[Wizard] Enviando solicitud de onboarding');
    this.userService.completeOnboarding(request).subscribe({
      next: async () => this.finishSuccessfulOnboarding(port),
      error: (error: any) => this.handleSubmissionError(error, port),
    });
  }

  private async finishSuccessfulOnboarding(port: WizardCompletionPort): Promise<void> {
    this.authService.setResolvedFlow('registered');
    try {
      const refreshed = await this.tokenVerifier.refreshAndWait(10_000);
      port.setLoading(false);
      if (!refreshed) {
        this.logger.warn(
          '[Wizard] Updated app_metadata or role claim not observed via silent refresh; forcing full login to obtain updated token'
        );
        try {
          await this.loginWithRedirectToHome();
          return;
        } catch (error) {
          this.logger.error(
            '[Wizard] Failed to trigger loginWithRedirect: ' + String(error),
            'Wizard.completeOnboarding'
          );
          port.setError(port.translate('wizard.save_error'));
          return;
        }
      }
    } catch (error) {
      this.logger.error(
        '[Wizard] Error while waiting for updated token: ' + String(error),
        'Wizard.completeOnboarding'
      );
      port.setLoading(false);
      port.setError(port.translate('wizard.save_error'));
      return;
    }
    this.router.navigate(['/home']);
  }

  private handleSubmissionError(error: any, port: WizardCompletionPort): void {
    const status = error?.status ?? 'unknown';
    const traceId = error?.error?.traceId ?? 'n/a';
    this.logger.error(
      `[Wizard] Error completing onboarding. status=${status} traceId=${traceId}`,
      'Wizard.completeOnboarding'
    );
    port.setLoading(false);
    if (!port.handleProvisioningFailure(error)) {
      port.setError(port.translate('wizard.save_error'));
    }
  }
}
